import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import i18n from '../i18n';
import brandLogo from '../assets/logo-centre-jour.png';
import LegalFooter from '../components/common/LegalFooter';

const LoginPage = () => {
  const { t } = useTranslation();
  const { login, verifyMfa, resendMfaCode } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [mfaMethod, setMfaMethod] = useState('email');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaState, setMfaState] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [brandLogoError, setBrandLogoError] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(form.email, form.password, mfaMethod);
      if (result?.mfaRequired) {
        setMfaState(result);
        setMfaCode('');
        return;
      }
      const user = result;
      if (user.mustChangePassword) navigate('/change-password');
      else navigate('/');
    } catch (err) {
      setError(err.response?.data?.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleMfaVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await verifyMfa({ mfaToken: mfaState.mfaToken, code: mfaCode });
      if (user.mustChangePassword) navigate('/change-password');
      else navigate('/');
    } catch (err) {
      setError(err.response?.data?.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleMfaResend = async () => {
    setError('');
    setLoading(true);
    try {
      const result = await resendMfaCode({ mfaToken: mfaState.mfaToken, method: mfaMethod });
      setMfaState(result);
      setMfaCode('');
    } catch (err) {
      setError(err.response?.data?.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-left">
        <div className="login-box">
          <div className="login-logo">
            {!brandLogoError ? (
              <img
                src={brandLogo}
                alt="Logo Centre de Jour"
                className="login-brand-logo"
                onError={() => setBrandLogoError(true)}
              />
            ) : (
              <div className="login-logo-icon">🏥</div>
            )}
            <h1>Centre de Jour<br />Vivre en Santé</h1>
            <p>{t('auth.loginTitle')}</p>
            <div className="login-lang">
              <button type="button" className={`lang-btn ${i18n.language.startsWith('fr') ? 'active' : ''}`} aria-pressed={i18n.language.startsWith('fr')} onClick={() => i18n.changeLanguage('fr')}>Français</button>
              <button type="button" className={`lang-btn ${i18n.language.startsWith('en') ? 'active' : ''}`} aria-pressed={i18n.language.startsWith('en')} onClick={() => i18n.changeLanguage('en')}>Anglais</button>
            </div>
          </div>

          {error && <div className="alert alert-error" role="alert">⚠ {error}</div>}

          {!mfaState ? (
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label" htmlFor="login-email">{t('auth.email')}</label>
                <input
                  id="login-email"
                  type="email"
                  className="form-input"
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  required
                  autoFocus
                  placeholder="votre@courriel.ca"
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="login-password">{t('auth.password')}</label>
                <input
                  id="login-password"
                  type="password"
                  className="form-input"
                  value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  required
                  placeholder="••••••••"
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="login-mfa-method">Méthode MFA</label>
                <select id="login-mfa-method" className="form-select" value={mfaMethod} onChange={e => setMfaMethod(e.target.value)}>
                  <option value="email">{t('auth.email')}</option>
                  <option value="sms">SMS</option>
                </select>
              </div>

              <div style={{ textAlign: 'right', marginTop: -8, marginBottom: 16 }}>
                <Link
                  to="/forgot-password"
                  style={{ fontSize: 13, color: 'var(--primary)', textDecoration: 'none' }}
                >
                  {t('auth.forgotPassword')}
                </Link>
              </div>

              <button type="submit" className="btn btn-primary w-full btn-lg" disabled={loading}
                style={{ width: '100%', justifyContent: 'center', marginTop: 8 }}>
                {loading ? t('common.loading') : t('auth.login')}
              </button>
            </form>
          ) : (
            <form onSubmit={handleMfaVerify}>
              <div className="alert alert-wrap" role="status" style={{ background: 'var(--primary-50)', border: '1px solid var(--primary-light)', marginBottom: 16 }}>
                <span>
                  Code envoyé par <strong>{mfaState.method === 'sms' ? 'SMS' : 'email'}</strong> à{' '}
                  <strong className="alert-destination">{mfaState.destination}</strong>
                </span>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="login-mfa-code">Code MFA (6 chiffres)</label>
                <input
                  id="login-mfa-code"
                  type="text"
                  className="form-input"
                  value={mfaCode}
                  onChange={e => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  autoFocus
                  placeholder="123456"
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="login-mfa-method-confirm">Méthode MFA</label>
                <select id="login-mfa-method-confirm" className="form-select" value={mfaMethod} onChange={e => setMfaMethod(e.target.value)}>
                  <option value="email">{t('auth.email')}</option>
                  <option value="sms">SMS</option>
                </select>
              </div>

              <button type="submit" className="btn btn-primary w-full btn-lg" disabled={loading}
                style={{ width: '100%', justifyContent: 'center', marginTop: 8 }}>
                {loading ? t('common.loading') : 'Valider le code'}
              </button>
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button type="button" className="btn btn-outline" onClick={handleMfaResend} disabled={loading} style={{ flex: 1 }}>
                  Renvoyer le code
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={loading}
                  style={{ flex: 1 }}
                  onClick={() => {
                    setMfaState(null);
                    setMfaCode('');
                  }}
                >
                  Retour
                </button>
              </div>
            </form>
          )}

          <p style={{ textAlign: 'center', marginTop: 20, fontSize: 12, color: 'var(--gray-400)' }}>
            staff.centredejourensante.ca
          </p>
          <LegalFooter />
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
